import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { visuallyHidden } from '@mui/utils';
import { ApiInstance } from '../../../api/api';
import { SwitchPortsDataContext } from '../../../context/switchPortsDataContext';
import {
  DeviceARPDataType,
  DeviceStackDataType,
  DeviceStackErrorDataType,
  DeviceSwitchPortsDataType,
  VlanDataType,
} from '../../../types/buildingTypes';
import { DataSourceType, Order, ActionLockEvent } from '../../../types/types';
import SwitchPortsSearch from '../../searches/SwitchPortsSearch';
import TablePlaceholder from '../../placeholders/TablePlaceholder';
import DeviceProcessingError from '../../placeholders/DeviceProcessingError';
import SwitchPortsFilterMenu, {
  DEFAULT_SWITCH_PORTS_FILTER_STATE,
} from '../../filters/SwitchPortsFilterMenu';
import { formatMacAddress } from '../../../utils/macFormatter';
import MacDataDialog from '../../dialogs/MacDataDialog';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import { useSearchParams } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import PortBounceAction from '../../dialogs/port-bounce-action/PortBounceAction';
import PortFlipAction from '../../dialogs/port-flip-action/PortFlipAction';
import PortActionValidationDialog from '../../dialogs/common/PortActionValidationDialog';
import { validatePort } from '../../../utils/portActionValidation';
import { validateMacMiniPortAction } from '../../../utils/macMiniValidation';
import DataSourceSwitcher from '../../switchers/DataSourceSwitcher';
import { alpha } from '@mui/material/styles';
import StackTable from '../distro/StackTable';
import DeviceButtonTooltip from '../../placeholders/DeviceButtonTooltip';
import TableNoDataPlaceholder from '../../placeholders/TableNoDataPlaceholder';
import TableNoResults from '../../placeholders/TableNoResults';
import s from '../../placeholders/DeviceProcessingError.module.css';
import Divider from '@mui/material/Divider';
import { red } from '@mui/material/colors';
import { stableSort, getComparator } from '../../../utils/tableHelpers';
import ActionLockTimeline from '../../dialogs/action-lock-timeline/ActionLockTimeline';

interface HeadSwitchPortsCell {
  disablePadding: boolean;
  id: keyof DeviceSwitchPortsDataType;
  label: string;
  numeric: boolean;
}

const headSwitchPortsCells: readonly HeadSwitchPortsCell[] = [
  {
    id: 'port',
    numeric: false,
    disablePadding: false,
    label: 'Port',
  },
  {
    id: 'profile',
    numeric: false,
    disablePadding: false,
    label: 'Profile',
  },
  {
    id: 'switch_serial',
    numeric: false,
    disablePadding: false,
    label: 'Serial',
  },
  {
    id: 'vlan',
    numeric: false,
    disablePadding: false,
    label: 'VLAN',
  },
  {
    id: 'status',
    numeric: false,
    disablePadding: false,
    label: 'Status',
  },
  {
    id: 'poe',
    numeric: false,
    disablePadding: false,
    label: 'POE',
  },
  {
    id: 'speed',
    numeric: false,
    disablePadding: false,
    label: 'Speed',
  },
  {
    id: 'uptime',
    numeric: false,
    disablePadding: false,
    label: 'Uptime',
  },
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: 'Description',
  },
  {
    id: 'mac_address',
    numeric: false,
    disablePadding: false,
    label: 'Mac address',
  },
];

interface AccessSwitchTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof DeviceSwitchPortsDataType
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  isApiMode: boolean;
  isJuniper: boolean;
  isMeraki: boolean;
}

function AccessSwitchTableHead(props: AccessSwitchTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    isApiMode,
    isJuniper,
    isMeraki,
  } = props;

  const filteredSwitchPortsCells = headSwitchPortsCells.filter((item: HeadSwitchPortsCell) => {
    if (item.id === 'profile') return isApiMode && isJuniper;
    if (item.id === 'switch_serial') return isMeraki;
    return true;
  });

  const createSortHandler =
    (property: keyof DeviceSwitchPortsDataType) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'deselect all desserts',
            }}
          />
        </TableCell>
        {filteredSwitchPortsCells.map((HeadSwitchPortsCell) => (
          <TableCell
            key={HeadSwitchPortsCell.id}
            align={HeadSwitchPortsCell.numeric ? 'right' : 'left'}
            padding={HeadSwitchPortsCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === HeadSwitchPortsCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === HeadSwitchPortsCell.id}
              direction={orderBy === HeadSwitchPortsCell.id ? order : 'asc'}
              onClick={createSortHandler(HeadSwitchPortsCell.id)}
            >
              {HeadSwitchPortsCell.label}
              {orderBy === HeadSwitchPortsCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface TableToolbarProps {
  numSelected: number;
  selected: DeviceSwitchPortsDataType[] | undefined;
  isJuniper: boolean;
  isApiMode: boolean;
  isDistro: boolean;
  title: string;
  entity: string;
  search: boolean;
  navigationFilter?: boolean;
  navigationFilterName?: string;
  navigationFilterValue?: string;
  onOpenPortBounceActionDialog: () => void;
  onOpenPortFlipActionDialog: () => void;
  hasUnreportedConfiguration: boolean;
}

function TableToolbar(props: TableToolbarProps) {
  const {
    numSelected,
    selected,
    isJuniper,
    isApiMode,
    isDistro,
    title,
    entity,
    search,
    navigationFilter,
    navigationFilterName,
    navigationFilterValue,
    onOpenPortBounceActionDialog,
    onOpenPortFlipActionDialog,
    hasUnreportedConfiguration,
  } = props;
  const [filterInput, setFilterInput] = React.useState(DEFAULT_SWITCH_PORTS_FILTER_STATE);

  const portsString = selected?.map((item, index) => `${index + 1}) ${item.port}`).join(', ');

  return (
    <>
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <Typography sx={{ flex: '1 1 100%' }} variant="h5" id="tableTitle" component="div">
          {title}: {entity}
        </Typography>
        {search && <SwitchPortsSearch isApiMode={isApiMode} />}
        <SwitchPortsFilterMenu
          filterState={filterInput}
          setFilterState={setFilterInput}
          navigationFilter={navigationFilter}
          navigationFilterName={navigationFilterName}
          navigationFilterValue={navigationFilterValue}
          isApiMode={isApiMode}
        />
        <Button
          onClick={onOpenPortBounceActionDialog}
          size={'small'}
          variant="contained"
          color="primary"
          disabled={!selected || selected.length === 0}
          sx={{ ml: 1, px: 3, py: 1 }}
        >
          Bounce
        </Button>
        {!(isJuniper && !isApiMode && !isDistro) && (
          <Button
            onClick={onOpenPortFlipActionDialog}
            size={'small'}
            variant="contained"
            color="primary"
            disabled={!selected || selected.length === 0}
            sx={{ ml: 1, px: 3, py: 1 }}
          >
            Config
          </Button>
        )}
      </Toolbar>
      {hasUnreportedConfiguration && isApiMode && isJuniper ? (
        <Toolbar
          sx={{
            pl: { sm: 2 },
            pr: { xs: 1, sm: 1 },
          }}
        >
          <Typography color={red[700]}>
            * The interface configuration has been updated in the Juniper dashboard, but has not yet
            been applied to the device.
          </Typography>
        </Toolbar>
      ) : null}

      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          bgcolor: (theme) =>
            numSelected > 0
              ? alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity)
              : 'transparent',
        }}
      >
        <Typography
          sx={{ flex: '1 1 100%', opacity: numSelected > 0 ? 1 : 0 }}
          color="inherit"
          variant="subtitle1"
          component="div"
          aria-live="polite"
          aria-hidden={numSelected === 0 ? true : undefined}
        >
          {numSelected} {numSelected === 1 ? 'port' : 'ports'} selected: <b>{portsString}</b>
        </Typography>
      </Toolbar>
    </>
  );
}

interface AccessSwitchTypeTableProps {
  device: string;
  deviceIP: string;
  selectedRegion: string;
  selectedBuilding: string;
  selectedVendor: string;
  selectedSite: string;
  unlimitedPortBounce: boolean;
  unlimitedPortFlip: boolean;
}

interface FilterMap {
  [key: string]: string;
}

export default function AccessSwitchTable(props: AccessSwitchTypeTableProps) {
  const {
    device,
    deviceIP,
    selectedRegion,
    selectedBuilding,
    selectedVendor,
    selectedSite,
    unlimitedPortBounce,
    unlimitedPortFlip,
  } = props;
  const isJuniper: boolean = selectedVendor === 'juniper';
  const isMeraki: boolean = selectedVendor === 'meraki';
  const isDistro: boolean = device === 'distro';
  const [isLoading, setIsLoading] = React.useState(false);
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof DeviceSwitchPortsDataType>('port');
  const [selected, setSelected] = React.useState<DeviceSwitchPortsDataType[]>([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [allSwitchPorts, setAllSwitchPorts] = React.useState<DeviceSwitchPortsDataType[]>([]);
  const [rowsPerPage, setRowsPerPage] = React.useState(30);
  const [switchPorts, setSwitchPorts] = React.useState<DeviceSwitchPortsDataType[]>([]);
  const [stackData, setStackData] = React.useState<
    DeviceStackDataType[] | DeviceStackErrorDataType
  >([]);
  const [vlanData, setVlanData] = React.useState<VlanDataType | null>(null);
  const [actionLockHistory, setActionLockHistory] = React.useState<ActionLockEvent[]>([]);
  const [apiProcessingErrorFlag, setApiProcessingErrorFlag] = React.useState<Boolean>(false);
  const [stackApiProcessingErrorFlag, setStackApiProcessingErrorFlag] =
    React.useState<Boolean>(false);
  const [stackLegacyDeviceError, setStackLegacyDeviceError] = React.useState<Boolean>(false);
  const [loadingState, setLoadingState] = React.useState<Boolean>(false);
  const [open, setOpen] = React.useState(false);
  const [macData, setMacData] = React.useState([]);
  const [selectedPort, setSelectedPort] = React.useState<string>('');
  const [searchParams] = useSearchParams();
  const [navigationFilter, setNavigation] = React.useState(false);
  const [navigationFilterName, setNavigationFilterName] = React.useState<string>('');
  const [navigationFilterValue, setNavigationFilterValue] = React.useState<string>('');
  const [selectionLimitExceeded, setSelectionLimitExceeded] = React.useState(false);
  const [openPortBounceAction, setOpenPortBounceAction] = React.useState(false);
  const [openPortFlipAction, setOpenPortFlipAction] = React.useState(false);
  const [openPortActionValidationDialog, setOpenPortActionValidationDialog] = React.useState(false);
  const [portActionValidationDialogTitle, setPortActionValidationDialogTitle] =
    React.useState<string>('');
  const [invalidPorts, setInvalidPorts] = React.useState<string[]>([]);
  const [macMiniArpData, setMacMiniArpData] = React.useState<DeviceARPDataType[]>([]);
  const [dataSourceType, setDataSourceType] = React.useState<DataSourceType>(
    isMeraki || (isJuniper && !isDistro) ? 'api' : 'cli'
  );
  const filterMap: FilterMap = {
    'filter[port]': 'port',
    'filter[mac_address]': 'mac_address',
  };
  const isApiMode: boolean = dataSourceType === 'api';
  const hasUnreportedConfiguration: boolean = allSwitchPorts.some(
    (port) => port.unreported_configuration
  );

  const clearStates = () => {
    setAllSwitchPorts([]);
    setSwitchPorts([]);
    setStackData([]);
    setSelected([]);
    setVlanData(null);
    setActionLockHistory([]);
    setMacMiniArpData([]);
  };

  const extractNavigationFilterParams = (searchParams: URLSearchParams, filterMap: FilterMap) => {
    for (const [key, value] of Array.from(searchParams.entries())) {
      if (key.startsWith('filter') && filterMap[key]) {
        return {
          navigationFilter: true,
          navigationFilterName: filterMap[key],
          navigationFilterValue: value,
        };
      }
    }

    return {
      navigationFilter: false,
      navigationFilterName: '',
      navigationFilterValue: '',
    };
  };

  const getSwitchPorts = async () => {
    setIsLoading(true);
    clearStates();

    const { navigationFilter, navigationFilterName, navigationFilterValue } =
      extractNavigationFilterParams(searchParams, filterMap);
    const legacyDeviceResponse: string = 'Invalid input detected at \'^\' marker.';
    const api_mode: boolean = dataSourceType === 'api';

    try {
      const switch_stack = await ApiInstance.getSwitch(
        deviceIP,
        selectedVendor,
        selectedBuilding,
        selectedRegion,
        device,
        selectedSite,
        api_mode
      );

      setAllSwitchPorts(switch_stack.switch_ports_data);
      setSwitchPorts(switch_stack.switch_ports_data);
      setStackData(switch_stack.stack_data);
      setActionLockHistory(switch_stack.lock_history);
      setMacMiniArpData(switch_stack.mac_mini_arp_data || []);

      const shouldSort = isMeraki || (isJuniper && (isApiMode || isDistro));
      const availableVlans = shouldSort
        ? [...switch_stack.available_vlans].sort(
          (itemA, itemB) =>
            (itemA.vlan as any) - (itemB.vlan as any) || itemA.vlan.localeCompare(itemB.vlan)
        )
        : switch_stack.available_vlans;

      if (isApiMode && isJuniper) {
        setVlanData({
          type: 'juniper',
          available_vlans: availableVlans,
        });
      } else {
        setVlanData({
          type: 'classic',
          available_vlans: availableVlans,
        });
      }

      setNavigation(navigationFilter);
      setNavigationFilterName(navigationFilterName);
      setNavigationFilterValue(navigationFilterValue);
      setStackData(switch_stack.stack_data?.error === true ? [] : switch_stack.stack_data);
      setStackApiProcessingErrorFlag(switch_stack.stack_data?.error === true);
      setStackLegacyDeviceError(switch_stack.stack_data?.detail === legacyDeviceResponse);
      setRowsPerPage(switch_stack.switch_ports_data.length);
    } catch (e: any) {
      setStackApiProcessingErrorFlag(true);
      setApiProcessingErrorFlag(true);
    }

    setIsLoading(false);
  };

  React.useEffect(() => {
    getSwitchPorts();
  }, [dataSourceType]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof DeviceSwitchPortsDataType
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelected([]);
  };

  // Reason a DISTRO row's checkbox is disabled, or '' if selectable: see
  // validateMacMiniPortAction for the ARP-based eligibility rules.
  const getMacMiniRowBlockReason = (row: DeviceSwitchPortsDataType): string => {
    const result = validateMacMiniPortAction(row, allSwitchPorts, macMiniArpData);
    return result.ok ? '' : result.reason || '';
  };

  const isPortFlippable = (row: DeviceSwitchPortsDataType): boolean =>
    !isDistro || getMacMiniRowBlockReason(row) === '';

  const selectionCap = isDistro ? 1 : unlimitedPortBounce && unlimitedPortFlip ? 10 : 5;

  const handleClick = (event: React.MouseEvent<unknown>, id: DeviceSwitchPortsDataType) => {
    if (!isPortFlippable(id)) return;

    const selectedIndex = selected.indexOf(id);
    let newSelected: DeviceSwitchPortsDataType[] = [];

    if (selectedIndex === -1) {
      if (selected.length >= selectionCap) {
        setSelectionLimitExceeded(true);
        return;
      }

      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const openMacDataDialog = (data: any, portName: string) => {
    setMacData(data);
    setSelectedPort(portName);
    setOpen(true);
  };

  const closeMacDataDialog = () => {
    setOpen(false);
    setMacData([]);
    setSelectedPort('');
  };

  const handleOpenPortBounceActionDialog = () => {
    const invalidPortsList: string[] = [];

    selected.forEach((item) => {
      const portData = item;

      if (portData) {
        const invalidReasons = validatePort(selectedVendor, portData);
        if (invalidReasons.length > 0) {
          invalidPortsList.push(`${item.port} - ${invalidReasons.join(', ')}`);
        }
      }
    });

    if (invalidPortsList.length > 0) {
      setInvalidPorts(invalidPortsList);
      setPortActionValidationDialogTitle('Bounce');
      setOpenPortActionValidationDialog(true);
    } else {
      setOpenPortBounceAction(true);
    }
  };

  const handleOpenPortFlipActionDialog = () => {
    const invalidPortsList: string[] = [];

    selected.forEach((item) => {
      const portData = item;

      if (portData) {
        const invalidReasons = validatePort(selectedVendor, portData);
        if (invalidReasons.length > 0) {
          invalidPortsList.push(`${item.port} - ${invalidReasons.join(', ')}`);
        }
      }
    });

    if (invalidPortsList.length > 0) {
      setInvalidPorts(invalidPortsList);
      setPortActionValidationDialogTitle('Flip');
      setOpenPortActionValidationDialog(true);
    } else {
      setOpenPortFlipAction(true);
    }
  };

  const handleClosePortBounceAction = () => {
    setOpenPortBounceAction(false);
  };

  const handleClosePortFlipAction = () => {
    setOpenPortFlipAction(false);
  };

  const handleClosePortActionValidationDialog = () => setOpenPortActionValidationDialog(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const isSelected = (id: DeviceSwitchPortsDataType) => selected.indexOf(id) !== -1;

  const showActionLockTimeline = actionLockHistory.length > 0;

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - switchPorts.length) : 0;

  const switchPortsRows = React.useMemo(
    () =>
      stableSort(switchPorts, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, page, rowsPerPage, switchPorts]
  );

  const column_labels = headSwitchPortsCells
    .filter((column) => {
      if (column.id === 'profile') return isApiMode && isJuniper;
      if (column.id === 'switch_serial') return isMeraki;
      return true;
    })
    .map((item) => item.label);

  const renderMacAddresses = (mac_addresses: string) =>
    Object.values(mac_addresses).map((mac_address) => (
      <Typography variant="body2">{formatMacAddress(mac_address)}</Typography>
    ));

  const getRowTooltipContent = (
    port: string,
    configuredProfile: string,
    configuredDescription: string,
    dynamicPortConfiguration: string
  ): React.ReactNode => (
    <React.Fragment>
      <Typography variant="body2" className={s.row_header_tooltip}>
        Configuration state for <b>{port}</b>
      </Typography>
      <Divider className={s.host_divider_tooltip} />
      <Typography variant="body2" className={s.row_body_tooltip}>
        Configured profile: {configuredProfile || 'N/A'} <br />
        Configured description: {configuredDescription || 'N/A'}
        <br />
        Dynamic port configuration: {dynamicPortConfiguration || 'N/A'}
      </Typography>
    </React.Fragment>
  );

  return (
    <SwitchPortsDataContext.Provider
      value={{
        allSwitchPorts,
        switchPorts,
        loadingState,
        setSwitchPorts,
        setLoadingState,
      }}
    >
      {isJuniper && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <DataSourceSwitcher
            selected={dataSourceType}
            onChange={setDataSourceType}
            disabled={isDistro}
          />
        </Box>
      )}
      <StackTable
        device={device}
        selectedSite={selectedSite}
        arpEntries={stackData}
        isLoading={isLoading}
        apiErrorFlag={stackApiProcessingErrorFlag}
        legacyDeviceError={stackLegacyDeviceError}
        isMeraki={isMeraki}
      />
      {showActionLockTimeline && !isDistro && (
        <Box sx={{ margin: '5pt' }}>
          <ActionLockTimeline events={actionLockHistory} />
        </Box>
      )}
      <Box sx={{ width: '100%', margin: '5pt' }} id={'switch-ports'}>
        <Paper sx={{ width: '100%', mb: 2 }}>
          <TableToolbar
            numSelected={selected.length}
            selected={selected}
            isJuniper={isJuniper}
            isApiMode={isApiMode}
            isDistro={isDistro}
            title={`Switch ports for selected ${device}`}
            entity={`${selectedSite}`}
            search={true}
            navigationFilter={navigationFilter}
            navigationFilterName={navigationFilterName}
            navigationFilterValue={navigationFilterValue}
            onOpenPortBounceActionDialog={handleOpenPortBounceActionDialog}
            onOpenPortFlipActionDialog={handleOpenPortFlipActionDialog}
            hasUnreportedConfiguration={hasUnreportedConfiguration}
          />
          <Snackbar
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            autoHideDuration={5000}
            open={selectionLimitExceeded}
            onClose={() => setSelectionLimitExceeded(false)}
          >
            <Alert
              onClose={() => setSelectionLimitExceeded(false)}
              severity="info"
              sx={{
                fontSize: '1.2rem',
                padding: '10px',
              }}
            >
              {`Sorry, you can select up to ${selectionCap} port${selectionCap !== 1 ? 's' : ''} only!`}
            </Alert>
          </Snackbar>
          {isLoading ? (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <TablePlaceholder rows={10} columns={column_labels} />
            </Box>
          ) : (
            <TableContainer sx={{ minHeight: '30rem' }}>
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={dense ? 'small' : 'medium'}
              >
                <AccessSwitchTableHead
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={switchPorts.length}
                  isApiMode={isApiMode}
                  isJuniper={isJuniper}
                  isMeraki={isMeraki}
                />
                <TableBody>
                  {switchPortsRows.map((row, index) => {
                    const isItemSelected = isSelected(row);
                    const labelId = `switch-ports-table-checkbox-${index}`;
                    const macMiniRowBlockReason = isDistro ? getMacMiniRowBlockReason(row) : '';

                    return (
                      <DeviceButtonTooltip
                        title={getRowTooltipContent(
                          row.port,
                          row.configured_profile,
                          row.configured_description,
                          row.dynamic_port_configuration
                        )}
                        PopperProps={{
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -9],
                              },
                            },
                          ],
                        }}
                        disableHoverListener={!isApiMode}
                      >
                        <TableRow
                          hover
                          onClick={(event) => handleClick(event, row)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.port}
                          selected={isItemSelected}
                          sx={{
                            cursor: macMiniRowBlockReason === '' ? 'pointer' : 'default',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Tooltip title={macMiniRowBlockReason}>
                              <span tabIndex={0}>
                                <Checkbox
                                  color="primary"
                                  checked={isItemSelected}
                                  disabled={macMiniRowBlockReason !== ''}
                                  inputProps={{
                                    'aria-labelledby': labelId,
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          {isApiMode && isJuniper ? (
                            <TableCell component="th" id={labelId} scope="row" padding="normal">
                              {row.port}
                              {row.port_parent?.trim() && ` (${row.port_parent})`}
                              {row.port_children?.trim() && ` [${row.port_children}]`}
                              {row.unreported_configuration && (
                                <Typography
                                  component="span"
                                  display="inline"
                                  color="error"
                                  fontWeight="bold"
                                  sx={{ padding: 0, margin: 0 }}
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: 200,
                                  }}
                                >
                                  *
                                </Typography>
                              )}
                            </TableCell>
                          ) : (
                            <TableCell component="th" id={labelId} scope="row" padding="normal">
                              {row.port}
                            </TableCell>
                          )}
                          {isApiMode && isJuniper ? (
                            <TableCell
                              style={{
                                whiteSpace: 'nowrap',
                                overflowX: 'auto',
                              }}
                            >
                              {row.profile}
                              {row.dynamic && (
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: '#bbdefb',
                                    color: '#0d47a1',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    padding: '1px 4px',
                                    borderRadius: '6px',
                                    whiteSpace: 'nowrap',
                                    marginLeft: '2px',
                                  }}
                                >
                                  Dynamic
                                </Box>
                              )}
                            </TableCell>
                          ) : null}
                          {isMeraki ? (
                            <TableCell
                              style={{
                                whiteSpace: 'nowrap',
                                overflowX: 'auto',
                              }}
                            >
                              {row.switch_serial} ({row.switch_name})
                            </TableCell>
                          ) : null}
                          <TableCell>{row.vlan}</TableCell>
                          <TableCell>{row.status}</TableCell>
                          <TableCell>{row.poe}</TableCell>
                          <TableCell>{row.speed}</TableCell>
                          <TableCell>{row.uptime}</TableCell>
                          <TableCell>{row.description}</TableCell>
                          {(() => {
                            if (row.mac_address.length > 1) {
                              return (
                                <TableCell>
                                  <Button
                                    variant="outlined"
                                    endIcon={<DynamicFeedIcon fontSize={'large'} />}
                                    onClick={() => openMacDataDialog(row.mac_address, row.port)}
                                  >
                                    multiple
                                  </Button>
                                </TableCell>
                              );
                            } else {
                              return <TableCell>{renderMacAddresses(row.mac_address)}</TableCell>;
                            }
                          })()}
                        </TableRow>
                      </DeviceButtonTooltip>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: (dense ? 33 : 53) * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                  {allSwitchPorts.length === 0 && !apiProcessingErrorFlag && (
                    <TableRow>
                      <TableCell colSpan={headSwitchPortsCells.length}>
                        <TableNoDataPlaceholder entity="Access switch" />
                      </TableCell>
                    </TableRow>
                  )}
                  {switchPorts.length === 0 && allSwitchPorts.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={headSwitchPortsCells.length}>
                        <TableNoResults />
                      </TableCell>
                    </TableRow>
                  )}
                  {apiProcessingErrorFlag && (
                    <TableRow>
                      <TableCell colSpan={headSwitchPortsCells.length}>
                        <DeviceProcessingError />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            rowsPerPageOptions={[10, 20, 30, { value: switchPorts.length, label: 'All' }]}
            component="div"
            count={switchPorts.length}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage="Rows per table:"
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <FormControlLabel
          control={<Switch checked={dense} onChange={handleChangeDense} />}
          label="Dense padding"
        />
      </Box>
      <PortActionValidationDialog
        open={openPortActionValidationDialog}
        onClose={handleClosePortActionValidationDialog}
        action={portActionValidationDialogTitle}
        vendor={selectedVendor}
        invalidPorts={invalidPorts}
      />
      <PortBounceAction
        open={openPortBounceAction}
        onClose={handleClosePortBounceAction}
        unlimitedPortBounce={!!unlimitedPortBounce}
        ip_address={deviceIP}
        device_type={selectedVendor}
        region={selectedRegion}
        building_code={selectedBuilding}
        unit={device}
        unit_name={selectedSite}
        selectedPorts={selected}
        isJuniper={isJuniper}
        isMeraki={isMeraki}
        isApiMode={isApiMode}
        isDistro={isDistro}
      />
      <PortFlipAction
        open={openPortFlipAction}
        onClose={handleClosePortFlipAction}
        unlimitedPortFlip={unlimitedPortFlip}
        ipAddress={deviceIP}
        deviceType={selectedVendor}
        region={selectedRegion}
        buildingCode={selectedBuilding}
        unit={device}
        unitName={selectedSite}
        selectedPorts={selected}
        availableVlans={vlanData}
        isJuniper={isJuniper}
        isMeraki={isMeraki}
        isApiMode={isApiMode}
        isDescriptionEditable={!isDistro}
        isDistro={isDistro}
      />
      <MacDataDialog
        open={open}
        handleClose={closeMacDataDialog}
        portName={selectedPort}
        macData={macData}
      />
    </SwitchPortsDataContext.Provider>
  );
}
